import {
  Box,
  Checkbox,
  CollapsibleCard,
  ConfirmationDialogBox,
  Divider,
  Grid,
} from '@atgfr8/atg-ui-tailwind';
import {
  CustomerLTLOverrideCarrierDetails,
  ltlCustomerSetting,
} from '@store/customers/customerDetails/LTL';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { isLTLAdmin } from '@utils/utils';
import { CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID } from '@views/order/constants';
import { size, uniq } from 'lodash';
import { Accessor, createSignal, For, Setter, Show } from 'solid-js';

import { customerSettingLabels, emailAlertLabels } from '../../constants';
import { InitialLTLFormValues } from '../Ltl';
import { customerSettingAndAlert as classes } from './classes';

type SettingConfig = {
  key: keyof InitialLTLFormValues;
  label: string;
  disabledWhenNotAdmin?: boolean;
  hiddenWhenNotAdmin?: boolean;
  requiresConfirmation?: boolean;
};

const CUSTOMER_SETTING_CONFIGS: SettingConfig[] = [
  { key: 'enableLtlInCustomerPortal', label: customerSettingLabels.EnableLTL },
  {
    key: 'enableCustomerPortalAutoDispatch',
    label: customerSettingLabels.AllowAutoDispatch,
  },
  {
    key: 'enableCustomerSpecificPricing',
    label: customerSettingLabels.EnableCSP,
    disabledWhenNotAdmin: true,
  },
  { key: 'enablePrePro', label: customerSettingLabels.EnablePrePro },
  {
    key: 'enableAgentQuotesInCustomerPortal',
    label: customerSettingLabels.EnableAgentQuotesInCp,
  },
  {
    key: 'enableSuggestedClass',
    label: customerSettingLabels.EnableSuggestedClassInCustomerPortal,
    requiresConfirmation: true,
  },
  {
    key: 'enableMixedCommodities',
    label: customerSettingLabels.EnableMixedCommodities,
  },
  {
    key: 'disableBolInCustomerPortal',
    label: customerSettingLabels.DisableBolInCustomerPortal,
  },
  {
    key: 'insuranceTermsAccepted',
    label: customerSettingLabels.InsuranceTermsAccepted,
    hiddenWhenNotAdmin: true,
  },
];

const EMAIL_ALERT_CONFIGS: SettingConfig[] = [
  {
    key: 'emailAgentIfNoQuoteResult',
    label: emailAlertLabels.EmailAgentIfNoQuoteResult,
  },
  {
    key: 'emailAgentIfShipmentNotBuilt',
    label: emailAlertLabels.EmailAgentIfShipmentNotBuilt,
  },
];

const CustomerSettingAndAlert = (props: {
  setFields: (key: string, value: unknown) => void;
  data: Accessor<InitialLTLFormValues>;
  setEnableLTLWarnings: (value: Array<string>) => void;
  setIsDirty: Setter<boolean>;
  setIsSettingsDirty: Setter<boolean>;
  onSectionChange: () => void;
  showQuoteExpiration: boolean;
}) => {
  let warnings: string[] = [];
  const [quoteExpirationError, setQuoteExpirationError] = createSignal<
    string | null
  >(null);

  const updateSetting = (key: keyof InitialLTLFormValues, val: boolean) => {
    if (key === 'enableLtlInCustomerPortal' && validateEnableLTL() && val)
      return;
    props.setIsDirty(true);
    props.setIsSettingsDirty(true);
    props.onSectionChange();
    props.setFields(key, val);
  };

  const validateEnableLTL = () => {
    warnings = [];
    props.setEnableLTLWarnings([]);
    //Default
    validateAmount(props.data().ltlCostPlus.amount as number);
    validateMinimum(
      props.data().ltlCostPlus.minimum as number,
      props.data().ltlCostPlus.type,
    );
    validateMaximum(
      props.data().ltlCostPlus.maximum as number,
      props.data().ltlCostPlus.minimum as number,
      props.data().ltlCostPlus.amount as number,
      props.data().ltlCostPlus.type,
    );

    //Overrides
    if (size(props.data().ltlCostPlusOverrides) > 0) {
      props.data().ltlCostPlusOverrides.forEach((x) => {
        validateAmount(x.amount as number);
        validateMinimum(x.minimum as number, x.type);
        validateMaximum(
          x.maximum as number,
          x.minimum as number,
          x.amount as number,
          x.type,
        );
        validateOverrideContainsCarrier(x.ltlCostPlusOverrideCarriers);
      });
    }
    if (size(warnings) > 0) {
      props.setEnableLTLWarnings(uniq(warnings));
      document
        .getElementById('customer-detail-page-header')
        ?.scrollIntoView({ behavior: 'smooth' });

      return true;
    }
    return false;
  };

  const validateAmount = (amount: number) => {
    if (amount < 0) {
      warnings.push('Amount cannot be negative.');
    } else if (amount === 0) {
      warnings.push('Amount must be greater than 0 to enable LTL.');
    }
  };

  const validateMinimum = (amount: number, type: string) => {
    if (amount < 0 && type === 'Percentage') {
      warnings.push('Minimum cannot be a negative value.');
    }
  };

  const validateMaximum = (
    maximum: number,
    minimum: number,
    amount: number,
    type: string,
  ) => {
    if (type === 'Percentage') {
      if (maximum < minimum) {
        warnings.push(
          'Maximum must be equal to or greater than Minimum when using percentage type.',
        );
      }
      if (maximum === 0 && amount !== 0) {
        warnings.push(
          'Maximum must be greater than 0 when using percentage type.',
        );
      }
    }
  };

  const validateOverrideContainsCarrier = (
    carriers: CustomerLTLOverrideCarrierDetails[],
  ) => {
    if (size(carriers) < 1) {
      warnings.push(
        'A Margin Override must have a minimum of 1 assigned carrier.',
      );
    }
  };

  return (
    <CollapsibleCard
      startTitle="Customer Settings and Email Alerts"
      loading={ltlCustomerSetting.isLoading}
      class="mb-2"
    >
      <Grid container class="overflow-hidden!">
        <Grid item size={{ md: 12, lg: 6 }} class="min-w-0!">
          <Box>Customer Settings</Box>
          <Divider class={classes.dividerStyle} />
          <Box class={classes.mainText}>
            Enable customer use of LTL quoting, automatic carrier dispatching,
            and Customer Specific Pricing.
          </Box>

          <Box class={classes.checkboxContainer}>
            <For
              each={CUSTOMER_SETTING_CONFIGS.filter(
                (setting) => !setting.hiddenWhenNotAdmin || isLTLAdmin(),
              )}
            >
              {(setting) => (
                <Checkbox
                  label={setting.label}
                  checked={props.data()[setting.key] as boolean}
                  class="[&>span]:text-xs [&>span]:whitespace-normal! [&>span]:break-words!"
                  onChange={(val) => {
                    if (setting.requiresConfirmation && val) {
                      openDialogBox(
                        CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID,
                      );
                      return;
                    }
                    updateSetting(setting.key, val);
                  }}
                  disabled={setting.disabledWhenNotAdmin && !isLTLAdmin()}
                  color="info"
                  dataTestId={`customer-setting-and-alert-${setting.key}-checkboxInput`}
                />
              )}
            </For>
          </Box>
        </Grid>
        <Grid item size={{ md: 12, lg: 6 }} class="min-w-0!">
          <Box>Email Alerts</Box>
          <Divider class="mt-1!" />
          <Box class={classes.mainText}>Get email notifications when:</Box>
          <Box class={classes.checkboxContainer}>
            <For each={EMAIL_ALERT_CONFIGS}>
              {(setting) => (
                <Checkbox
                  class="ml-[-9px] [&>span]:text-xs [&>span]:whitespace-normal! [&>span]:break-words!"
                  size="xs"
                  label={setting.label}
                  checked={props.data()[setting.key] as boolean}
                  onChange={(val) => updateSetting(setting.key, val)}
                  color="info"
                  dataTestId={`customer-setting-and-alert-${setting.key}-checkboxInput`}
                />
              )}
            </For>
          </Box>
        </Grid>
      </Grid>
      {props.showQuoteExpiration && (
        <>
          <Divider class="mt-3! mb-2!" />
          <Box>Quote Expiration Timeframe</Box>
          <Box class={classes.mainText}>
            Override the default LTL quote expiration period for this customer.
            Leave blank to use the system default.
          </Box>
          <Show when={isLTLAdmin()}>
            <Box class="flex items-center gap-3 mt-2">
              <input
                type="number"
                min="1"
                step="1"
                class={`border rounded px-2 py-1 text-sm w-24 ${quoteExpirationError() ? 'border-red-500' : 'border-[#ccc]'}`}
                placeholder="Days"
                name="quoteExpirationDays"
                value={props.data().quoteExpirationDays ?? ''}
                onInput={(e) => {
                  const raw = e.currentTarget.value;
                  if (raw === '') {
                    setQuoteExpirationError(null);
                    props.setIsDirty(true);
                    props.setIsSettingsDirty(true);
                    props.onSectionChange();
                    props.setFields('quoteExpirationDays', null);
                    return;
                  }
                  const parsed = parseInt(raw, 10);
                  if (
                    !Number.isInteger(parsed) ||
                    String(parsed) !== raw.trim() ||
                    parsed <= 0
                  ) {
                    setQuoteExpirationError('Must be a positive whole number.');
                    return;
                  }
                  setQuoteExpirationError(null);
                  props.setIsDirty(true);
                  props.setIsSettingsDirty(true);
                  props.onSectionChange();
                  props.setFields('quoteExpirationDays', parsed);
                }}
              />
              <Box class="text-sm text-[#606060]">days</Box>
              <Show when={quoteExpirationError()}>
                <Box class="text-xs text-red-600">{quoteExpirationError()}</Box>
              </Show>
            </Box>
          </Show>
          <Show when={!isLTLAdmin()}>
            <Box class="mt-2 text-sm text-[#606060]">
              {props.data().quoteExpirationDays != null
                ? `${props.data().quoteExpirationDays} days`
                : 'System default'}
            </Box>
          </Show>
        </>
      )}
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID,
        )}
        dataTestId={CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID}
        id={CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID}
        title=""
        onSubmit={() => {
          updateSetting('enableSuggestedClass', true);
          closeDialogBox(
            CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID,
          );
        }}
        onCancel={() => {
          updateSetting('enableSuggestedClass', false);
          closeDialogBox(
            CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID,
          );
        }}
        onSubmitText="I ACKNOWLEDGE"
        onSubmitBtnClass="bg-[#248670]! text-black!"
        closeDialogBox={(_reason) =>
          closeDialogBox(
            CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID,
          )
        }
      >
        <span class="text-[#000000] text-base font-normal">
          The suggested class is determined based on the density of the items
          entered for an LTL quote. It is your responsibility to select the
          correct class to ensure accurate quoting from the LTL carriers.
          <br />
          Please review the requested items for quoting with your customer and
          verify the correct class for each item.
        </span>
      </ConfirmationDialogBox>
    </CollapsibleCard>
  );
};

export default CustomerSettingAndAlert;
